<script setup>
import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});
const emit = defineEmits(['open']);
const { t } = useI18n();
const { accountId } = useAccount();
const { run, abort, isPending } = useAbortableRequest();
const messages = ref([]);
const messageList = ref(null);
const nextBeforeId = ref(null);
const loaded = ref(false);
const error = ref(false);

const clear = () => {
  messages.value = [];
  nextBeforeId.value = null;
  loaded.value = false;
  error.value = false;
};

const load = async (more = false) => {
  const beforeId = more ? nextBeforeId.value : null;
  if (more && !beforeId) return;
  const previousHeight = messageList.value?.scrollHeight || 0;
  const previousTop = messageList.value?.scrollTop || 0;
  error.value = false;
  await run(async signal => {
    try {
      const { data } = await ContactAPI.getMessages(props.contactId, beforeId, {
        signal,
      });
      if (signal.aborted) return;
      messages.value = more
        ? [...data.payload, ...messages.value]
        : data.payload;
      nextBeforeId.value = data.next_before_id;
      loaded.value = true;
      await nextTick();
      if (signal.aborted) return;
      if (messageList.value) {
        messageList.value.scrollTop = more
          ? messageList.value.scrollHeight - previousHeight + previousTop
          : messageList.value.scrollHeight;
      }
    } catch (failure) {
      if (signal.aborted) return;
      clear();
      error.value = true;
    }
  });
};

watch(
  [accountId, () => props.contactId],
  () => {
    abort();
    clear();
    if (props.contactId) load();
  },
  { immediate: true }
);

const attachmentUrl = url =>
  typeof url === 'string' &&
  (/^https?:\/\//i.test(url) || (url.startsWith('/') && !url.startsWith('//')));
const originLabel = origin =>
  [
    origin.inbox_name,
    origin.phone_number ||
      origin.email_address ||
      origin.channel_type?.split('::').pop(),
  ]
    .filter(Boolean)
    .join(' · ');
const messageDate = timestamp => new Date(timestamp * 1000).toLocaleString();
</script>

<template>
  <section
    :aria-label="t('CONTACT_PANEL.CONVERSATIONS.TIMELINE')"
    class="min-w-0"
  >
    <div class="flex items-center justify-between gap-2 px-4 py-2">
      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ t('CONTACT_PANEL.CONVERSATIONS.TIMELINE') }}
      </h3>
      <Button
        variant="ghost"
        size="sm"
        icon="i-lucide-refresh-cw"
        :aria-label="t('CONTACT_PANEL.CONVERSATIONS.REFRESH')"
        :disabled="isPending"
        @click="load()"
      />
    </div>
    <Button
      v-if="nextBeforeId"
      variant="ghost"
      size="sm"
      class="mx-4 mb-2"
      :disabled="isPending"
      :label="t('CONTACT_PANEL.CONVERSATIONS.OLDER')"
      @click="load(true)"
    />
    <ol
      ref="messageList"
      class="m-0 max-h-[32rem] list-none space-y-3 overflow-y-auto px-4 pb-3"
    >
      <li
        v-for="message in messages"
        :key="message.id"
        class="min-w-0 rounded-lg border border-n-weak p-3"
      >
        <div
          class="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-n-slate-11"
        >
          <span>
            {{ `${originLabel(message.origin)} · #${message.conversation_id}` }}
          </span>
          <time :datetime="new Date(message.created_at * 1000).toISOString()">
            {{ messageDate(message.created_at) }}
          </time>
        </div>
        <div class="mb-1 flex flex-wrap gap-2 text-xs text-n-slate-11">
          <span v-if="message.sender?.name">{{ message.sender.name }}</span>
          <span v-if="message.private">{{
            t('CONTACT_PANEL.CONVERSATIONS.PRIVATE')
          }}</span>
        </div>
        <p
          v-if="message.content"
          class="m-0 whitespace-pre-wrap break-words text-sm text-n-slate-12"
        >
          {{ message.content }}
        </p>
        <div
          v-if="message.attachments?.length"
          class="mt-2 flex flex-wrap gap-2"
        >
          <template
            v-for="(attachment, index) in message.attachments"
            :key="attachment.id"
          >
            <a
              v-if="attachmentUrl(attachment.data_url)"
              :href="attachment.data_url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs text-n-blue-11 underline"
            >
              {{
                t('CONTACT_PANEL.CONVERSATIONS.ATTACHMENT', {
                  number: index + 1,
                })
              }}
            </a>
          </template>
        </div>
        <button
          type="button"
          class="mt-2 text-xs text-n-blue-11 underline focus-visible:outline-2"
          @click="
            emit(
              'open',
              {
                id: message.conversation_id,
                inbox_id: message.origin.inbox_id,
              },
              $event,
              message.id
            )
          "
        >
          {{ t('CONTACT_PANEL.CONVERSATIONS.OPEN_MESSAGE') }}
        </button>
      </li>
    </ol>
    <div v-if="isPending" class="flex justify-center py-3"><Spinner /></div>
    <p v-if="error" role="alert" class="m-0 px-4 py-3 text-xs text-n-ruby-11">
      {{ t('CONTACT_PANEL.CONVERSATIONS.TIMELINE_ERROR') }}
    </p>
    <p
      v-else-if="loaded && !messages.length"
      class="m-0 px-4 py-3 text-xs text-n-slate-11"
    >
      {{ t('CONTACT_PANEL.CONVERSATIONS.TIMELINE_EMPTY') }}
    </p>
  </section>
</template>
